import { PrimaryButton as EtPrimaryButton, Size } from "@ever-tutor/et-ui";
import { useId, type ComponentProps } from "react";

const PrimaryButton = ({
  onClick,
  size = Size.MD,
  children,
  type = "button",
  disabled = false,
  mobileFullWidth = false,
  className = "",
  id: providedId,
  "aria-label": label,
  "aria-labelledby": labelledBy,
  ...buttonProps
}: {
  children: React.ReactNode;
  onClick?: () => void;
  size?: Size | "xs" | "sm" | "md" | "lg";
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  mobileFullWidth?: boolean;
  className?: string;
} & Omit<ComponentProps<typeof EtPrimaryButton>, "size">) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  // The package requires an explicit accessible name. A self-reference keeps
  // rich button content (icons + text, loading labels) as its native name.
  const accessibility =
    label !== undefined
      ? { "aria-label": label }
      : { "aria-labelledby": labelledBy || id };
  const sizeMap: Record<string, Size> = {
    xs: Size.XS,
    sm: Size.SM,
    md: Size.MD,
    lg: Size.LG,
  };

  const finalSize = typeof size === "string" ? sizeMap[size] : size;

  return (
    <EtPrimaryButton
      onClick={onClick}
      size={finalSize}
      type={type}
      disabled={disabled}
      mobileFullWidth={mobileFullWidth}
      className={`et-primary ${className}`}
      id={id}
      {...accessibility}
      {...buttonProps}
    >
      {children}
    </EtPrimaryButton>
  );
};

export default PrimaryButton;
